import { Injectable, effect, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../../../core/services/api.service';
import type { AgentInvite, JoinTicket, Presence, PresenceStatus } from '../agent-request.models';

/**
 * How often an ONLINE agent asks "is a demo ringing for me?".
 *
 * This same request stamps the presence heartbeat and nudges the server's ring forward, so the
 * interval is also what decides how promptly a lapsed offer moves to the next agent. Three
 * seconds against a ten-second offer window: prompt enough to be invisible, cheap enough to run
 * on every open CRM tab.
 */
const POLL_INTERVAL_MS = 3_000;

const SILENT = { addAccessToken: true, showLoader: false, showErrorToaster: false } as const;

/**
 * The agent's side of "Ask to Agent": their availability, and the demo currently ringing them.
 *
 * Root-provided and stateful on purpose. Two components read it — the toggle in the Tracking Demo
 * header and the ringing modal mounted in the private layout — and they never talk to each other:
 * the toggle writes `status`, and this service starts or stops the poll in response.
 *
 * NOTHING POLLS UNLESS SOMEBODY IS ONLINE. An OFFLINE user's browser makes exactly one request
 * (the initial presence read) for the whole session, so the feature costs nothing for the
 * colleagues who never handle demos.
 */
@Injectable({ providedIn: 'root' })
export class AgentRequestApiService {
  private readonly api = inject(ApiService);

  /**
   * The caller's own availability. OFFLINE until `boot()` says otherwise.
   *
   * There is no `busy` signal to go with it. "In a meeting" is the server's bookkeeping, and a
   * client copy would go stale the moment the server freed the agent — the toggle would keep
   * claiming "on a call" long after the call ended. The toggle says only what the user chose.
   */
  readonly status = signal<PresenceStatus>('OFFLINE');
  /** The demo ringing right now, or null. */
  readonly invite = signal<AgentInvite | null>(null);

  private timer: ReturnType<typeof setInterval> | null = null;
  private booted = false;
  private inFlight = false;

  constructor() {
    // The toggle only ever sets `status`; the poll follows it. Neither component knows the other.
    effect(() => (this.status() === 'ONLINE' ? this.startPolling() : this.stopPolling()));
  }

  /** Read the caller's presence once per app load. Safe to call from several components. */
  async boot(): Promise<void> {
    if (this.booted) return;
    this.booted = true;
    try {
      await this.loadPresence();
    } catch {
      // A failed read must not claim the user is offline when they may be online — leave the
      // default and stay quiet. The next toggle writes an authoritative value anyway.
    }
  }

  async loadPresence(): Promise<Presence> {
    const presence = await firstValueFrom(
      this.api.get<Presence>('live-call-agent/presence/me', undefined, SILENT),
    );
    this.apply(presence);
    return presence;
  }

  /** Only ever affects the authenticated user — the backend ignores any pid in the request. */
  async setPresence(status: PresenceStatus): Promise<Presence> {
    const presence = await firstValueFrom(
      this.api.patch<Presence>('live-call-agent/presence/me', { status }, SILENT),
    );
    this.apply(presence);
    if (status === 'OFFLINE') this.invite.set(null);
    return presence;
  }

  /**
   * Take the meeting. Returns the identity to join the room under.
   *
   * A 409 means somebody else got there first, or the ten seconds ran out mid-click — the caller
   * should just clear the modal rather than treat it as a failure.
   */
  async accept(requestId: number): Promise<JoinTicket> {
    const ticket = await firstValueFrom(
      this.api.post<JoinTicket>(`live-call-agent/requests/${requestId}/accept`, {}, SILENT),
    );
    this.invite.set(null);
    return ticket;
  }

  /** Decline. The server rings the next agent at once, without waiting out the window. */
  async reject(requestId: number): Promise<void> {
    this.invite.set(null); // optimistic: the modal must not linger while the request flies
    await firstValueFrom(this.api.post(`live-call-agent/requests/${requestId}/reject`, {}, SILENT));
  }

  // ── the poll ─────────────────────────────────────────────────────────────

  private startPolling(): void {
    if (this.timer) return;
    void this.pollInbox();
    this.timer = setInterval(() => void this.pollInbox(), POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  private async pollInbox(): Promise<void> {
    // A slow poll must not stack up behind itself, or a stalled network would fire a burst of
    // requests the moment it recovers.
    if (this.inFlight) return;
    this.inFlight = true;
    try {
      const invite = await firstValueFrom(
        this.api.get<AgentInvite | null>('live-call-agent/inbox', undefined, SILENT),
      );
      this.invite.set(invite ?? null);
    } catch {
      // Transient failures are expected (sleep, tab throttling, a deploy). Keep the last known
      // invite; if it really lapsed, the next successful poll clears it and the countdown in the
      // modal has already run out anyway.
    } finally {
      this.inFlight = false;
    }
  }

  private apply(presence: Presence): void {
    this.status.set(presence.status);
  }
}
